import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import SubscriptionInvoices from './pages/SubscriptionInvoices';
import Subscriptions from './pages/Subscriptions';
import MarketingPrograms from './pages/MarketingPrograms';
import MarketingChannels from './pages/MarketingChannels';
import ReviewFeedbacks from './pages/ReviewFeedbacks';
import MarketingProgramDetail from './pages/MarketingProgramDetail';
import MarketingDashboard from './pages/MarketingDashboard';
import CompanyMap from './pages/CompanyMap';
import Affiliate from './pages/Affiliate';
import AffiliateCompanyDetail from './pages/AffiliateCompanyDetail';
import StoreItems from './pages/StoreItems';
import StoreInvoices from './pages/StoreInvoices';
import SubscriptionVouchers from './pages/SubscriptionVouchers';
import AdminUsers from './pages/AdminUsers';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import SalesDashboard from './pages/SalesDashboard';
import Bundles from './pages/Bundles';
import ManualPurchases from './pages/ManualPurchases';
import QrisRegistrations from './pages/QrisRegistrations';
import MenuImports from './pages/MenuImports';
import { validateEnvironment } from './utils/env';

const ROLE_HOME: Record<string, string> = { sales: '/sales-dashboard', marketing: '/marketing-dashboard' };

// Confined Route - redirects role-confined users away from pages they aren't allowed on
const ConfinedRoute: React.FC<{ allow?: string[]; children: React.ReactNode }> = ({ allow = [], children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const role = user?.adminRole;
  if (role && ROLE_HOME[role] && !allow.includes(role)) {
    return <Navigate to={ROLE_HOME[role]} replace />;
  }
  return <Layout>{children}</Layout>;
};

// Manage Route Component - only accessible to full admins (canManage)
const ManageRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, canManage } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canManage) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/" element={
        <ConfinedRoute allow={[]}>
          <Dashboard />
        </ConfinedRoute>
      } />
      <Route path="/review-feedbacks" element={
        <ConfinedRoute allow={[]}>
          <ReviewFeedbacks />
        </ConfinedRoute>
      } />
      <Route path="/companies" element={
        <ConfinedRoute allow={['sales', 'marketing']}>
          <Companies />
        </ConfinedRoute>
      } />
      <Route path="/companies/:companyId" element={
        <ConfinedRoute allow={['sales', 'marketing']}>
          <CompanyDetail />
        </ConfinedRoute>
      } />
      <Route path="/leads" element={
        <ConfinedRoute allow={['sales', 'marketing']}>
          <Leads />
        </ConfinedRoute>
      } />
      <Route path="/leads/:id" element={
        <ConfinedRoute allow={['sales', 'marketing']}>
          <LeadDetail />
        </ConfinedRoute>
      } />
      <Route path="/sales-dashboard" element={
        <ConfinedRoute allow={['sales']}>
          <SalesDashboard />
        </ConfinedRoute>
      } />
      <Route path="/marketing-dashboard" element={
        <ConfinedRoute allow={['marketing']}>
          <MarketingDashboard />
        </ConfinedRoute>
      } />
      <Route path="/subscription-invoices" element={
        <ConfinedRoute allow={['marketing']}>
          <SubscriptionInvoices />
        </ConfinedRoute>
      } />
      <Route path="/subscriptions" element={
        <ConfinedRoute allow={[]}>
          <Subscriptions />
        </ConfinedRoute>
      } />
      <Route path="/marketing-programs" element={
        <ConfinedRoute allow={[]}>
          <MarketingPrograms />
        </ConfinedRoute>
      } />
      <Route path="/marketing-programs/:programId" element={
        <ConfinedRoute allow={[]}>
          <MarketingProgramDetail />
        </ConfinedRoute>
      } />
      <Route path="/marketing-channels" element={
        <ConfinedRoute allow={['sales', 'marketing']}>
          <MarketingChannels />
        </ConfinedRoute>
      } />
      <Route path="/company-map" element={
        <ConfinedRoute allow={['sales', 'marketing']}>
          <CompanyMap />
        </ConfinedRoute>
      } />
      <Route path="/affiliate" element={
        <ConfinedRoute allow={[]}>
          <Affiliate />
        </ConfinedRoute>
      } />
      <Route path="/affiliate/company/:companyId" element={
        <ConfinedRoute allow={[]}>
          <AffiliateCompanyDetail />
        </ConfinedRoute>
      } />
      <Route path="/store-items" element={
        <ConfinedRoute allow={[]}>
          <StoreItems />
        </ConfinedRoute>
      } />
      <Route path="/store-invoices" element={
        <ConfinedRoute allow={[]}>
          <StoreInvoices />
        </ConfinedRoute>
      } />
      <Route path="/subscription-vouchers" element={
        <ConfinedRoute allow={[]}>
          <SubscriptionVouchers />
        </ConfinedRoute>
      } />
      <Route path="/bundles" element={
        <ConfinedRoute allow={[]}>
          <Bundles />
        </ConfinedRoute>
      } />
      <Route path="/manual-purchases" element={
        <ConfinedRoute allow={[]}>
          <ManualPurchases />
        </ConfinedRoute>
      } />
      <Route path="/qris-registrations" element={
        <ConfinedRoute allow={[]}>
          <QrisRegistrations />
        </ConfinedRoute>
      } />
      {/* Hanya admin full + readonly (sales/marketing dialihkan; backend juga 403). */}
      <Route path="/menu-imports" element={
        <ConfinedRoute allow={[]}>
          <MenuImports />
        </ConfinedRoute>
      } />
      <Route path="/admin-users" element={
        <ManageRoute>
          <AdminUsers />
        </ManageRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // Validate environment variables on app startup
    validateEnvironment();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
};

export default App;
